import React, { useState } from 'react';
import { Card, NavItem, TabContent, TabPane, NavLink, Container, Nav, CardHeader, Row, Col } from "reactstrap";
import Layout from "../../HorizontalMenu/Menu";
import Navbar from "../../HorizontalMenu/Navbar";
import classnames from "classnames";
import '../../global/GlobalCSS.css'
import GeneralSettings from './VesselMachinery/GeneralSettings/MachineryGeneralSetings';
import SFOC from './SFOC';
import Engines from './Engines';
import FuelMachineryConfiguration from './FuelMachineryConfiguration';
import SpecialOperations from './SpecialOperations/SpecialOperations';
import LubeOilMaster from './LubeOil/LubeOilMaster';
import VesselDetailsHeader from '../../Components/VesselDetailsHeader';
import VesselMachineryConfig from './VesselMachinery/VesselMachineryConfig';
import { useDispatch, useSelector } from 'react-redux';
import { VesselState, setVesselMasterPageURL, setVesselState } from "../../Store/Generic/ReportingSlice";
import Other from './Other/Other';
import { OnboardingStatus, Roles, tabNames, VesselConfigrationTabs, VesselTypeConstant } from 'shared/constants';
import { RootState } from 'index';
import VesselFuelConfiguration from 'VesselMaster/VesselConfiguration/VesselFuelConfiguration/VesselFuelConfiguration';
import VesselData from './VesselData';
import PopUp from 'Components/PopUp';
import SaveBeforeChange from 'VesselMaster/SaveBeforeChange';
import AdditionalConfiguration from './SensorMaster/SensorMaster';
import { FormikProps } from 'formik';
import { useDocumentTitle } from 'Components/useDocument.hooks';
import { Get } from 'VesselMaster/vesselMaster.hooks';
import { queryKeyes } from 'shared/queryKeys';
import { useQuery } from 'react-query';
import CargoTankDetails from './CargoTankDetails';
import SpeedAndConsumption from './SpeedAndConsumption/SpeedAndConsumption';
import { hasRole } from 'utils/auth/authUtils';
import { Stack } from '@mui/system';
import Skeleton from '@mui/material/Skeleton';
import FormLoading from 'Components/FormLoading';
import { isConfigurationButtonDisabled } from 'GenericForms/Helper';

const VesselConfiguration = () => {
    /** State variables start */
    const [activeSettingsTab, setActiveSettingsTab] = useState(VesselConfigrationTabs.VESSEL_DATA);
    const dispatch = useDispatch();
    const { VesselID, Vessels } = useSelector((state: RootState) => state.Reporting);
    const vessel = Vessels.find((rec: { [key: string]: string | number | boolean | Date | null }) => rec.id === VesselID);
    const [refreshVesselMachineries, setRefreshVesselMachineries] = useState(0);
    const [deletePopOverActive, setDeletePopOverActive] = useState<boolean>(false);
    const [checkValuesBeforeSwitch, setCheckValuesBeforeSwitch] = useState(false);
    const [saveBeforeChangePopUp, setStateSaveBeforeChangePopUp] = useState(false);
    const [tabName, setTabName] = useState('');
    const [nextTab, setNextTab] = useState<number | null>(null);
    const [formik, setFormik] = useState<FormikProps<any>>(null);
    const [refreshForm, setRefreshForm] = useState(false);
    const [disableYesButton, setDisableYesButton] = useState(false);
    const tabTitle = tabNames[activeSettingsTab] || "Vessel Configuration";
    useDocumentTitle(`${tabTitle} - Vessel Configuration - ecoSAIL`, "ecoSAIL");
    const operationStatus = window.location.pathname === '/onboarding' ? 'false' : 'true';
    /** State variables end */

    // Fetch configuration status data
    const { data: configurationData, isLoading } = useQuery(
        [queryKeyes.vessel.ConfigurationList.key, VesselID],
        async () => {
            return await Get(queryKeyes.vessel.ConfigurationList.url(VesselID), VesselID);
        },
        {
            enabled: true,
            staleTime: 0,
        }
    );

    // Helper function to get status for a tab
    const getTabStatus = (tabId: number) => {
        if (!configurationData) return 'pending';
        const tabData = configurationData.find((item: any) => item.tab_constant === tabId);
        return tabData?.status || 'pending';
    };

    // Helper function to get sub_tabs for a tab
    const getSubTabsStatus = (tabId: number) => {
        if (!configurationData) return [];
        const tabData = configurationData.find((item: any) => item.tab_constant === tabId);
        return tabData?.sub_tabs || [];
    };

    // Helper function to render status icon
    const renderStatusIcon = (status: string) => {
        if (status === 'completed') {
            return <i className="bx bx-check-double text-success me-2" />;
        } else {
            return <i className="mdi mdi-clock-edit-outline text-warning me-2" />;
        }
    };

    const toggle = (tab: number) => {
        const isSameTab = activeSettingsTab === tab;
        const isManagerWithOnboardedVessel =
            hasRole(Roles.MANAGER) && vessel.onboarding_status !== OnboardingStatus.PENDING;
        if (checkValuesBeforeSwitch) {
            if (!isSameTab) {
                setActiveSettingsTab(tab);
            }
        } else {
            setNextTab(tab);
            if (isManagerWithOnboardedVessel) {
                setActiveSettingsTab(tab);
            } else {
                setStateSaveBeforeChangePopUp(true);
            }
        }
        // Force refresh the form regardless of path
        setRefreshForm(prev => !prev);
    };

    const handleSaveAndSwitchTab = async (formik: any) => {
        if (formik) {
            await formik?.submitForm();
            formik.initialValues = formik?.values
            formik?.resetForm({ values: formik.values });
            setCheckValuesBeforeSwitch(true); // Reset the unsaved changes flag
            setRefreshForm(!refreshForm);
            if (nextTab !== null) {
                setActiveSettingsTab(nextTab); // Switch to the next tab
                setNextTab(null); // Reset the next tab state
            }
        }
        setStateSaveBeforeChangePopUp(false); // Close the popup
    };

    const handleNoSaveAndSwitchTab = (formik: any) => {
        formik?.setValues(formik.initialValues);
        setRefreshForm(!refreshForm);
        setCheckValuesBeforeSwitch(true); // Reset the unsaved changes flag
        if (nextTab !== null) {
            setActiveSettingsTab(nextTab); // Switch to the next tab
            setNextTab(null); // Reset the next tab state
        }
        setStateSaveBeforeChangePopUp(false); // Close the popup
    };

    const handleVesselState = (record: VesselState) => {
        dispatch(setVesselState(record))
    }

    /** Applicable vessel types for special operations */
    const SpecialOperationsVesselType = {
        TANKER: 1,
        GAS_CARRIER: 2,
        CONTAINER: 3,
        LNG_CARRIER: 4,
        GENERAL_CARGO_SHIP: 6,
    }

    const tabItems = [
        { id: VesselConfigrationTabs.VESSEL_DATA, name: "Vessel Data" },
        { id: VesselConfigrationTabs.MACHINERY, name: "Machinery" },
        { id: VesselConfigrationTabs.GENERAL_SETTINGS, name: "General Settings" },
        { id: VesselConfigrationTabs.SFOC, name: "SFOC" },
        { id: VesselConfigrationTabs.VESSEL_FUEL, name: "Vessel Fuel" },
        { id: VesselConfigrationTabs.MACHINERY_FUEL, name: "Machinery Fuel" },
        { id: VesselConfigrationTabs.ENGINES, name: "Engines " },
        ...(Object.values(SpecialOperationsVesselType).includes(vessel?.vessel_type_precedence_id) ?
            [{ id: VesselConfigrationTabs.SPECIAL_OPERATIONS, name: "Special Operations" }] : []),
        { id: VesselConfigrationTabs.LUBE_OIL, name: "Lube Oil" },
        // { id: VesselConfigrationTabs.SENSOR, name: "Sensors" },
        { id: VesselConfigrationTabs.SPEED_CONSUMPTION, name: "Speed & Consumption" },
        ...(vessel?.vessel_type_precedence_id === VesselTypeConstant.LNG_CARRIER
            ? [{ id: VesselConfigrationTabs.CARGO_TANK_DETAILS, name: "Cargo Tank Details" }]
            : []),
        { id: VesselConfigrationTabs.OTHER, name: "Other" },
    ];

    return (
        <React.Fragment>
            <PopUp
                state={saveBeforeChangePopUp}
                setState={setStateSaveBeforeChangePopUp}
                body={
                    <SaveBeforeChange
                        tabName={tabName}
                        onYes={() => handleSaveAndSwitchTab(formik)} // Save and switch
                        onNo={() => {
                            handleNoSaveAndSwitchTab(formik);
                            setDisableYesButton(false)
                        }} // Discard and switch
                        disableYesButton={disableYesButton}
                    />
                }
                title="Alert"
                size="sm"
            />
            <React.Fragment>
                <Layout>
                    <Navbar />
                </Layout>
                <div className="page-content">
                    <Container fluid>
                        <Card>
                            <CardHeader className="px-2 py-0">
                                <Row className='pb-2'>
                                    <Col sm="2" className="d-flex align-items-center">
                                        <button
                                            color='primary'
                                            className='btn btn-primary'
                                            onClick={() => {
                                                handleVesselState(null);
                                                dispatch(setVesselMasterPageURL(queryKeyes.pagination.vesselListPage.url(operationStatus)));
                                            }}>
                                            <i className="bx bx-chevron-left me-1" /> Back
                                        </button>
                                    </Col>
                                    <Col sm="10">
                                        <VesselDetailsHeader />
                                    </Col>
                                </Row>
                            </CardHeader>
                            {vessel?.onboarding_status === OnboardingStatus.ONBOARDED && isConfigurationButtonDisabled(vessel) && (
                                <p className='m-2 text-danger'>
                                    <strong>Note:</strong> This vessel has already been configured. Further changes to its configuration are not permitted.
                                </p>
                            )}
                            {isLoading && (
                                <Row className="m-0">
                                    <Col md="2" >
                                        <Stack spacing={2}>
                                            <Skeleton variant="rectangular" height={40} />
                                            <Skeleton variant="rectangular" height={40} />
                                            <Skeleton variant="rectangular" height={40} />
                                            <Skeleton variant="rectangular" height={40} />
                                            <Skeleton variant="rectangular" height={40} />
                                            <Skeleton variant="rectangular" height={40} />
                                            <Skeleton variant="rectangular" height={40} />
                                            <Skeleton variant="rectangular" height={40} />
                                        </Stack>
                                    </Col>
                                    <Col md="10">
                                        <Skeleton variant="rectangular" height={40} />
                                        <FormLoading />
                                    </Col>
                                </Row>
                            )}
                            {!isLoading &&
                                <Row className="m-0">
                                    {/* Left side - Vertical tabs */}
                                    <Col md="2" className="p-0 vertical-tabs border">
                                        <Nav vertical className="border-0">
                                            {tabItems.map(tab => (
                                                <NavItem key={tab.id} className="vertical-tab-item">
                                                    <NavLink
                                                        className={classnames("nav-link-custom", "vertical-tab-link", { active: activeSettingsTab === tab.id })}
                                                        onClick={() => {
                                                            toggle(tab.id);
                                                            if (tab.id === VesselConfigrationTabs.MACHINERY_FUEL) {
                                                                setDeletePopOverActive(false);
                                                            }
                                                        }}
                                                    >
                                                        <div className="d-flex align-items-center">
                                                            {renderStatusIcon(getTabStatus(tab.id))}
                                                            <span className='text-wrap'>{tab.name}</span>
                                                        </div>
                                                    </NavLink>
                                                </NavItem>
                                            ))}
                                        </Nav>
                                    </Col>
                                    {/* Right side - Tab content */}
                                    <Col md="10" >
                                        <TabContent activeTab={activeSettingsTab}>
                                            <TabPane tabId={VesselConfigrationTabs.MACHINERY} className='margin-16'>
                                                <VesselMachineryConfig
                                                    refreshVesselMachineries={refreshVesselMachineries}
                                                    setRefreshVesselMachineries={setRefreshVesselMachineries}
                                                    subTabsStatus={getSubTabsStatus(VesselConfigrationTabs.MACHINERY)}
                                                    saveBeforeChangePopUp={saveBeforeChangePopUp}
                                                />
                                            </TabPane>
                                            <TabPane tabId={VesselConfigrationTabs.GENERAL_SETTINGS} className='margin-16'>
                                                <GeneralSettings
                                                    refreshVesselMachineries={refreshVesselMachineries}
                                                    setRefreshVesselMachineries={setRefreshVesselMachineries}
                                                    setCheckValuesBeforeSwitch={setCheckValuesBeforeSwitch}
                                                    setTabName={setTabName}
                                                    setFormik={setFormik}
                                                    formik={formik}
                                                    VesselConfActiveTab={activeSettingsTab}
                                                />
                                            </TabPane>
                                            <TabPane tabId={VesselConfigrationTabs.SFOC} className='margin-16'>
                                                <SFOC
                                                    refreshVesselMachineries={refreshVesselMachineries}
                                                    setRefreshVesselMachineries={setRefreshVesselMachineries}
                                                    VesselConfActiveTab={activeSettingsTab}
                                                    setCheckValuesBeforeSwitch={setCheckValuesBeforeSwitch}
                                                    setTabName={setTabName}
                                                    setFormik={setFormik}
                                                    formik={formik}
                                                    refreshForm={refreshForm}
                                                    saveBeforeChangePopUp={saveBeforeChangePopUp}
                                                />
                                            </TabPane>
                                            <TabPane tabId={VesselConfigrationTabs.ENGINES} className='margin-16'>
                                                <Engines
                                                    refreshVesselMachineries={refreshVesselMachineries}
                                                    setRefreshVesselMachineries={setRefreshVesselMachineries}
                                                    VesselConfActiveTab={activeSettingsTab}
                                                    setCheckValuesBeforeSwitch={setCheckValuesBeforeSwitch}
                                                    setTabName={setTabName}
                                                    setFormik={setFormik}
                                                    formik={formik}
                                                    setDisableYesButton={setDisableYesButton}
                                                    saveBeforeChangePopUp={saveBeforeChangePopUp}
                                                />
                                            </TabPane>
                                            <TabPane tabId={VesselConfigrationTabs.VESSEL_FUEL} className='margin-16'>
                                                <VesselFuelConfiguration
                                                    refreshVesselMachineries={refreshVesselMachineries}
                                                    setRefreshVesselMachineries={setRefreshVesselMachineries}
                                                    setCheckValuesBeforeSwitch={setCheckValuesBeforeSwitch}
                                                    setTabName={setTabName}
                                                    setFormik={setFormik}
                                                    formik={formik}
                                                    VesselConfActiveTab={activeSettingsTab}
                                                    refreshForm={refreshForm}
                                                />
                                            </TabPane>
                                            <TabPane tabId={VesselConfigrationTabs.MACHINERY_FUEL} className='margin-16'>
                                                <FuelMachineryConfiguration
                                                    refreshVesselMachineries={refreshVesselMachineries}
                                                    setRefreshVesselMachineries={setRefreshVesselMachineries}
                                                    setCheckValuesBeforeSwitch={setCheckValuesBeforeSwitch}
                                                    setTabName={setTabName}
                                                    setFormik={setFormik}
                                                    formik={formik}
                                                    VesselConfActiveTab={activeSettingsTab}
                                                    refreshForm={refreshForm}
                                                />
                                            </TabPane>
                                            <TabPane tabId={VesselConfigrationTabs.SPECIAL_OPERATIONS} className='margin-16'>
                                                <SpecialOperations
                                                    refreshVesselMachineries={refreshVesselMachineries}
                                                    setRefreshVesselMachineries={setRefreshVesselMachineries}
                                                    setCheckValuesBeforeSwitch={setCheckValuesBeforeSwitch}
                                                    setTabName={setTabName}
                                                    setFormik={setFormik}
                                                    formik={formik}
                                                    VesselConfActiveTab={activeSettingsTab}
                                                />
                                            </TabPane>
                                            <TabPane tabId={VesselConfigrationTabs.LUBE_OIL} className='margin-16'>
                                                <LubeOilMaster />
                                            </TabPane>
                                            <TabPane tabId={VesselConfigrationTabs.OTHER} className='margin-16'>
                                                <Other
                                                    VesselConfActiveTab={activeSettingsTab}
                                                    setCheckValuesBeforeSwitch={setCheckValuesBeforeSwitch}
                                                    setTabName={setTabName}
                                                    setFormik={setFormik}
                                                    formik={formik}
                                                    refreshForm={refreshForm}
                                                    setDisableYesButton={setDisableYesButton}
                                                    saveBeforeChangePopUp={saveBeforeChangePopUp}
                                                />
                                            </TabPane>
                                            <TabPane tabId={VesselConfigrationTabs.VESSEL_DATA} className='margin-16'>
                                                <VesselData
                                                    refreshVesselMachineries={refreshVesselMachineries}
                                                    setRefreshVesselMachineries={setRefreshVesselMachineries}
                                                    VesselConfActiveTab={activeSettingsTab}
                                                    setCheckValuesBeforeSwitch={setCheckValuesBeforeSwitch}
                                                    setTabName={setTabName}
                                                    setFormik={setFormik}
                                                    formik={formik}
                                                    setRefreshForm={setRefreshForm}
                                                    refreshForm={refreshForm}
                                                    setDisableYesButton={setDisableYesButton}
                                                    saveBeforeChangePopUp={saveBeforeChangePopUp}
                                                    subTabsStatus={getSubTabsStatus(VesselConfigrationTabs.VESSEL_DATA)}
                                                />
                                            </TabPane>
                                            <TabPane tabId={VesselConfigrationTabs.SPEED_CONSUMPTION} className='margin-16'>
                                                <SpeedAndConsumption
                                                    VesselConfActiveTab={activeSettingsTab}
                                                    deletePopOverActive={deletePopOverActive}
                                                    setDeletePopOverActive={setDeletePopOverActive}
                                                    setCheckValuesBeforeSwitch={setCheckValuesBeforeSwitch}
                                                    setTabName={setTabName}
                                                    setFormik={setFormik}
                                                    formik={formik}
                                                    setDisableYesButton={setDisableYesButton}
                                                    checkValuesBeforeSwitch={checkValuesBeforeSwitch}
                                                    subTabsStatus={getSubTabsStatus(VesselConfigrationTabs.SPEED_CONSUMPTION)}
                                                    saveBeforeChangePopUp={saveBeforeChangePopUp}
                                                />
                                            </TabPane>
                                            <TabPane tabId={VesselConfigrationTabs.CARGO_TANK_DETAILS} className='margin-16'>
                                                <CargoTankDetails
                                                    activeTab={activeSettingsTab}
                                                    setCheckValuesBeforeSwitch={setCheckValuesBeforeSwitch}
                                                    setTabName={setTabName}
                                                    setFormik={setFormik}
                                                    VesselConfActiveTab={activeSettingsTab}
                                                    refreshForm={refreshForm}
                                                />
                                            </TabPane>
                                            <TabPane tabId={VesselConfigrationTabs.SENSOR} className='margin-16'>
                                                <AdditionalConfiguration />
                                            </TabPane>
                                        </TabContent>
                                    </Col>
                                </Row>
                            }
                        </Card>
                    </Container>
                </div>
            </React.Fragment>
        </React.Fragment>
    )
}

export default VesselConfiguration;